import { gql } from '@apollo/client';

import {
  TAG_COUNT_FRAGMENT,
  TAG_HIGHLIGHTS_WITH_TAG_FRAGMENT,
} from './tags';

export const LIMIT = 20;

export const GET_PRACTICE_TESTS = gql`
  query PracticeTests($limit: Int, $offset: Int, $status: String, $search: String) {
    practiceTests(limit: $limit, offset: $offset, status: $status, search: $search) {
      id
      practiceTestStatus
      completedAt
      tester {
        id
        project {
          id
        }
        user {
          id
          email
          facebookId
          googleId
          linkedinId
          profile {
            id
            firstName
            lastName
            country
          }
        }
      }
    }
  }
`;

export const GET_PRACTICE_TEST_BY_USER = gql`
  query UserPracticeTest($userId: ID!) {
    userPracticeTest(userId: $userId) {
      id
      practiceTestStatus
      completedAt
      tester {
        id
        project {
          id
        }
        user {
          id
          status
          profile {
            id
            email
            firstName
            lastName
            gender
            birthdate
            country
            smartphone
            computer
            tablet
            state
            paypalAccount
            raceEthnicHeritage
            annualHouseholdIncome
            workStatus
            languages
            educationLevel
            jobCategories{
              id
              name
            }
            currentPosition{
              title
              seniority
              company{
                size
                industry
              }
            }
          }
        }
      }
    }
  }
`;

export const MARK_TEST_SESSION_AS_VIEWED = gql`
  mutation MarkTestSessionAsViewed($testSessionId: ID!) {
    markTestSessionAsViewed(testSessionId: $testSessionId) {
      testSession {
        id
        newVideo
        tester {
          id
        }
      }
    }
  }
`;

export const SPEAKER_TAG_HIGHLIGHTS_FRAGMENT = gql`
  fragment SpeakerTagHighlights on TestSessionSpeaker {
    tagHighlights {
      ...TagHighlightWithTag
    }
  }
  ${TAG_HIGHLIGHTS_WITH_TAG_FRAGMENT}
`;

export const TEST_SESSION_SPEAKER_FRAGMENT = gql`
  fragment TestSessionSpeakerFields on TestSessionSpeaker {
    id
    updatedAt
    text
    speakerTag
    videoStartTime
    videoEndTime
    transcriptHighlights {
      timeStart
      timeEnd
      text
    }
    ...SpeakerTagHighlights
  }
  ${SPEAKER_TAG_HIGHLIGHTS_FRAGMENT}
`;

export const ANSWER_TAG_HIGHLIGHTS_FRAGMENT = gql`
  fragment AnswerTagHighlights on TestSessionAnswer {
    tagHighlights {
      ...TagHighlightWithTag
    }
  }
  ${TAG_HIGHLIGHTS_WITH_TAG_FRAGMENT}
`;

export const TEST_SESSION_ANSWER_FRAGMENT = gql`
  fragment TestSessionAnswerFields on TestSessionAnswer {
    id
    updatedAt
    valueInteger
    valueText
    videoStartTime
    videoEndTime
    transcriptHighlights {
      timeStart
      timeEnd
      text
    }
    transcriptText
    question {
      id
      questionType
      label
    }
    questionOptions{
      id
    }
    transcript {
      results {
        alternatives {
          confidence
          transcript
        }
      }
    }
    ...AnswerTagHighlights
  }
  ${ANSWER_TAG_HIGHLIGHTS_FRAGMENT}
`;

export const TESTER_RATING_FRAGMENT = gql`
  fragment TesterRating on TestSession {
    rating
    ratingDescription
    ratingPrivate
  }
`;

export const TEST_SESSION_FRAGMENT = gql`
  fragment FullTestSession on TestSession {
    id
    videoKey
    videoUrl
    status
    approved
    removed
    transcriptText {
      text
      speakerTag
      startTime
    }
    transcript {
      results {
        alternatives {
          confidence
          transcript
          words {
            word
            speakerTag
            startTime {
              seconds
            }
          }
        }
      }
    }
    testSessionAnswers {
      ...TestSessionAnswerFields
    }
    testSessionSpeakers {
      ...TestSessionSpeakerFields
    }
  }
  ${ANSWER_TAG_HIGHLIGHTS_FRAGMENT}
  ${TEST_SESSION_SPEAKER_FRAGMENT}
  ${TEST_SESSION_ANSWER_FRAGMENT}
`;

export const GET_TEST_SESSION = gql`
  query TestSession ($id: ID!){
    testSession (id: $id) {
      ...FullTestSession
    }
  }
  ${TEST_SESSION_FRAGMENT}
`;

// moderated
export const EDIT_TRANSCRIPT = gql`
  mutation EditTranscript($transcript: [TranscriptTextAttributes!]!, $testSessionId: ID!) {
    editTranscript(transcript: $transcript, testSessionId: $testSessionId) {
      testSession {
        ...FullTestSession
      }
    }
  }
  ${TEST_SESSION_FRAGMENT}
`;

export const EDIT_TEST_SESSION_SPEAKER = gql`
  mutation EditTestSessionSpeaker($id: ID!, $attributes: TestSessionSpeakerAttributes!) {
    editTestSessionSpeaker(id: $id, attributes: $attributes) {
      testSessionSpeaker {
        ...TestSessionSpeakerFields
      }
    }
  }
  ${TEST_SESSION_SPEAKER_FRAGMENT}
`;

export const EDIT_ANSWER_TRANSCRIPT = gql`
  mutation EditAnswerTranscript($transcript: String!, $testSessionAnswerId: ID!) {
    editAnswerTranscript(transcriptText: $transcript, testSessionAnswerId: $testSessionAnswerId) {
      testSession {
        id
        testSessionAnswers {
          id
          transcriptText
          ...AnswerTagHighlights
        }
      }
    }
  }
  ${ANSWER_TAG_HIGHLIGHTS_FRAGMENT}
`;

export const START_STUDY_MUTATION = gql`
  mutation StartStudyMutation($projectId: ID!, $segmentId: ID!) {
    startStudy(projectId: $projectId, segmentId: $segmentId) {
      studyLink
      testSession {
        id
      }
    }
  }
`;

export const updateAnswersTagHighlightsDeleted = (testSessionId, answerId, tagHighlight) => (cache) => {
  cache.evict({ id: `TagHighlight:${tagHighlight.id}` });
  cache.gc();
};

export const updateAnswersTagHighlightsCreated = (testSessionId, answerId) => (cache, mutationResult) => {
  const {
    data: {
      createTagHighlight: { tagHighlights },
    },
  } = mutationResult;

  const { tagHighlights: cachedHighlights } = cache.readFragment({
    id: `TestSessionAnswer:${answerId}`,
    fragment: ANSWER_TAG_HIGHLIGHTS_FRAGMENT,
    fragmentName: 'AnswerTagHighlights',
  });

  cache.writeFragment({
    id: `TestSessionAnswer:${answerId}`,
    fragment: ANSWER_TAG_HIGHLIGHTS_FRAGMENT,
    fragmentName: 'AnswerTagHighlights',
    data: {
      tagHighlights: [
        ...cachedHighlights,
        ...tagHighlights,
      ],
      __typename: 'TestSessionAnswer',
    },
  });
};

export const updateSpeakersTagHighlightsDeleted = (testSessionId, speakerId, tagHighlight) => (cache) => {
  let { tagHighlights } = cache.readFragment({
    id: `TestSessionSpeaker:${speakerId}`,
    fragment: SPEAKER_TAG_HIGHLIGHTS_FRAGMENT,
    fragmentName: 'SpeakerTagHighlights',
  });

  tagHighlights = tagHighlights.filter(({ id }) => id !== tagHighlight.id);

  cache.writeFragment({
    id: `TestSessionSpeaker:${speakerId}`,
    fragment: SPEAKER_TAG_HIGHLIGHTS_FRAGMENT,
    fragmentName: 'SpeakerTagHighlights',
    data: {
      tagHighlights,
      __typename: 'TestSessionSpeaker',
    },
  });

  const { count } = cache.readFragment({
    id: `Tag:${tagHighlight.tag.id}`,
    fragment: TAG_COUNT_FRAGMENT,
  });

  cache.writeFragment({
    id: `Tag:${tagHighlight.tag.id}`,
    fragment: TAG_COUNT_FRAGMENT,
    data: {
      count: count - 1,
      __typename: 'Tag',
    },
  });
};

export const updateSpeakersTagHighlightsCreated = (
  testSessionId,
  speakerId,
) => (cache, { data: { createTagHighlight: { tagHighlights } } }) => {
  const { tagHighlights: cachedHighlights } = cache.readFragment({
    id: `TestSessionSpeaker:${speakerId}`,
    fragment: SPEAKER_TAG_HIGHLIGHTS_FRAGMENT,
    fragmentName: 'SpeakerTagHighlights',
  });

  cache.writeFragment({
    id: `TestSessionSpeaker:${speakerId}`,
    fragment: SPEAKER_TAG_HIGHLIGHTS_FRAGMENT,
    fragmentName: 'SpeakerTagHighlights',
    data: {
      tagHighlights: [
        ...cachedHighlights,
        ...tagHighlights,
      ],
      __typename: 'TestSessionSpeaker',
    },
  });
};

// Get the test_session from "TesterQueryType" in the api
export const GET_TEST_SESSION_STATUS = gql`
  query TestSessions ($testSessionId: ID!){
    testSessions(testSessionId: $testSessionId) {
      id
      status
      expiresAt
      tester {
        mrId
        cintId
      }
    }
  }
`;

export const REPROCESS_VIDEO = gql`
  mutation ReprocessVideo($testSessionId: ID!) {
    reprocessVideo(testSessionId: $testSessionId) {
      testSession {
        id
        status
      }
    }
  }
`;

export const COMPLETED_TEST_SESSIONS_BY_USER = gql`
  query TestSessions($userId: ID!, $order: String!, $hidden: Boolean, $isCompleted: Boolean!) {
    testSessions(userId: $userId, order: $order, hidden: $hidden, isCompleted: $isCompleted) {
      id
      rating
      ratingDescription
      completedAt
      feedback
      tester {
        id
        hidden
        testerPayment {
          id
          status
          convertedAmount
        }
        project {
          id
          shareCode
          name
          company {
            id
            name
          }
        }
      }
    }
    user(userId: $userId) {
      id
      averageRating
      status
      profile {
        id
        firstName
        lastName
        country
      }
    }
  }
`;

export const VERIFY_TEST_SESSION_TIME_PAST_AND_ALLOW_REFUND = gql`
  query VerifyTestSessionTimePast($testSessionId: ID!) {
    verifyTestSessionTimePast(testSessionId: $testSessionId)
    testSessions(testSessionId: $testSessionId){
      bookingStatus
      tester{
        project{
          allowRefund
        }

      }
    }
  }
`;

export const UPDATE_TEST_SESSION_DID_NOT_SHOW = gql`
  mutation UpdateTestSessionDidNotShowMutation($id: ID!, $attributes: TestSessionDidNotShowAttributes!) {
    updateTestSessionDidNotShow(id: $id, attributes: $attributes) {
      testSession {
        id
        bookingStatus
      }
    }
  }
`;

export const TEST_SESSION = gql`
  query TestSession($id: ID!) {
    testSession(id: $id) {
      id
      isPreview
      expiresAt
      expirationCount
      recorderStarted
      authenticationToken
      practiceTestStatus
      tester {
        id
        mrId
        cintId
        user {
          id
          tester
          guest
        }
        project {
          id
          name
          description
          testType
          status
          sessionDuration
          audienceParticipants
          ndaRequired
          studyContext
          otherTesterRequirements
          participantsSource
          lockVersion
          sortType
          randomCategory
          sortRandom
          tooltipDescription
          uploadImage
          hideName
          instructionsContent
          welcomeContent
          thankYouContent
          instructionsTitle
          welcomeTitle
          thankYouTitle
          cardSortItems {
            id
            name
            description
            itemOrder
            imageKey
            imageUrl
          }
          cardSortCategories {
            id
            name
            isDefault
            categoryOrder
          }
          questionTemplate {
            id
            name
          }
          audienceAttributes {
            id
            audienceAttributeType
            audienceAttributeOptions {
              id
              value
            }
          }
          company {
            id
          }
          surveys {
            id
            surveyType
            questions {
              id
              label
              questionType
              groupId
              questionOrder
              leftLabel
              rightLabel
              blurVideo
              treeTestNodes {
                id
              }
              followUpQuestions{
                id
                label
                questionType
                leftLabel
                rightLabel
                questionOrder
                questionOptions {
                  id
                  label
                  disqualify
                  qualify
                }
              }
              questionOptions {
                id
                label
                disqualify
                qualify
                optionType
                order
              }
            }
          }
          treeTestNodes {
            id
            label
            parentId
            order
            projectId
          }
        }
        segment {
          device
          operatingSystem
          recorderType
          participantsLanguage
          audienceTemplate{
            id
            name
          }
        }
      }
    }
  }
`;
